import React, { useEffect, useState, useRef } from "react";
import ReactPaginate from 'react-paginate';
import { useParams } from "react-router-dom";
import {exportRecipientsToCSV, notifyError, notifySuccess} from '../../helper';
import {RECIPIENT_HEADER} from '../../constants/constants';
import { confirmAlert } from "react-confirm-alert"; // Import the library
import Header from "../Header";
import Sidebar from "../Sidebar";
import Footer from "../Footer";
import CampaignHeader from "./CampaignHeader";
import Recipient from "./Recipient";
import client from '../../api';
import BlockLoader from "../BlockLoader";

const CampaignRecipients = () => {
    const {campaignID} = useParams('');
    const api = client();
    const [payload, setPayload] = useState({
        campaignID:campaignID,
        "eventType":"",
        "text":"", 
        "listID":"", 
        "offset": 0
    });

    const [recipients, setRecipients] = useState({ contacts: [], display_keys: [] });
    const [actionsDisplay, setActionsDisplay] = useState(false);
    const [count, setCount] = useState(0);
    const [contactLists, setContactLists] = useState([]);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [offset, setOffset] = useState(0);
    const [forcePage, setForcePage] = useState(0);
    const [limit] = useState(50); 
    const [totalCount, setTotalCount] = useState(0); 
    const controllerRef = useRef(null); // Store controller reference
    const [pageLoader,setPageLoader] = useState(true);

    // Function to add isChecked status to each recipient
    const addCheckedStatus = (data) => {
        if (!data || !Array.isArray(data.contacts)) {
            setRecipients({ contacts: [], display_keys: [] });
            return;
        }

        const updatedData = data.contacts.map(contact => ({
            ...contact,
            isChecked: false
        }));

        setRecipients({ contacts: updatedData, display_keys: data.display_keys || [] });
    };

    // Select/Deselect all recipients
    const handleSelectAll = (e) => {
        const isChecked = e.target.checked;
        setIsAllChecked(isChecked);
        
        const updatedItems = recipients.contacts.map(contact => ({
            ...contact,
            isChecked
        }));

        setRecipients(prev => ({ ...prev, contacts: updatedItems }));
        setActionsDisplay(isChecked);
        setCount(updatedItems.filter(item => item.isChecked).length);
    };

    // Handle individual recipient checkbox toggle
    const handleCheckboxChange = (id) => {
        const updatedItems = recipients.contacts.map(item =>
            item._id === id ? { ...item, isChecked: !item.isChecked } : item
        );

        setIsAllChecked(updatedItems.every(item => item.isChecked));
        setActionsDisplay(updatedItems.some(item => item.isChecked));
        setRecipients(prev => ({ ...prev, contacts: updatedItems }));
        setCount(updatedItems.filter(item => item.isChecked).length);
    };

    // Download selected recipients as CSV
    const handleDownloadCSV = () => {
        const recipientsClone = structuredClone(recipients);
        const updateItems = (recipientsClone.contacts).filter(item => item.isChecked)
        //generate a csv report for selected rows 
        exportRecipientsToCSV({...recipientsClone,contacts:[...updateItems]});
    }
    // Get emails of selected recipients
    const getSelectedEmails = () => {
       const emails = (recipients.contacts).filter((row) => row.isChecked).map(row => row.email);
        return emails;
    }

    // Pause selected recipients
    const pauseSelectedRecipients = async() => {
        try{
            const selectedEmails = getSelectedEmails();
            const payload = {
                campaign_id: campaignID,
                email_list: selectedEmails,
                action:"pause"
            }
            const response = await api.post('/v1/campaign/contacts/playPause',payload);
            if(response.status === 200 || response.status === 201){
                //console.log('response',response);
                notifySuccess(`${selectedEmails.length} email(s) are paused successfully.`);
                setTimeout(()=>{
                    window.location.reload();
                },1500);                
            }
        } catch(error){
            if(error.status === 409) notifyError('The selected recipients are already in pause status.');
            if(error.status === 400) notifyError('Bad Request. Unable to pause recipients. Please try again.');
        }
    }

    // Handle filter changes
    const handleFilter = (event) => {
        setPayload(prev => ({ ...prev, [event.target.name]: event.target.value }));
        setOffset(0);
        setForcePage(0);
    };

    // Fetch recipient data
    const getRecipientData = async () => {
        // Abort previous request if exists
        if (controllerRef.current) {
            controllerRef.current.abort();
        }
    
        // Create new AbortController for this request
        controllerRef.current = new AbortController();
    
        try {
            setPageLoader(true);
            const response = await api.post('/v1/campaign/recipients/list', { ...payload, offset, limit }, {
                signal: controllerRef.current.signal // Pass signal to request
            });
    
            if (response.status === 200 || response.status === 201) {
                addCheckedStatus(response.data);
                setTotalCount(response.data.total_count);
            }
            setPageLoader(false);
        } catch (error) {
            if(error.status === 400) {
                notifyError('Bad Request. Unable to fetch recipients. Please try again.');
            } else if (error.name === 'CanceledError') {
                console.log("Request was canceled");
            } 
            setPageLoader(false);
        }
    };

    // Handle page change in pagination
    const onPageChange = (data) => {
        const newOffset = data.selected * limit;
        setOffset(newOffset);
        setForcePage(data.selected);
    };

    useEffect(() => {
        getRecipientData();
    }, [payload, offset]);

    useEffect(() => {
        const getCampaignContactLists = async () => {
            try {
                const response = await api.post('/v1/campaign/contactsList', { campaignID });
                if (response.status === 200 || response.status === 201) {
                    setContactLists(response.data.contacts_list);
                }
            } catch (error) {
                notifyError('Bad Request. Unable to fetch contact Lists. Please try again.');
            }
        };
        getCampaignContactLists();
    },[]);

    return(<>
        <Header/>
        <div className="main-page">
            <Sidebar/>
            <div className="campaign-pages-wrap">
                <CampaignHeader page={'recipients'}/>
                {actionsDisplay ? <div className="action-wrap">
                    <div className="selected-records">
                        {count} selected
                    </div>
                    <div className="action-group">
                        <button 
                            className="delete-btn" 
                            onClick={()=>{ 
                                confirmAlert({
                                    title: "Pause Recipient(s)...!",
                                    message: "Are you sure you want to pause the Recipient(s)?",
                                    buttons: [
                                        { label: "Pause", onClick: () => {pauseSelectedRecipients()} },
                                        { label: "Cancel", onClick: () => {console.log('Pause action canceled..!')}}
                                    ]
                                })
                            }}>Pause</button>
                        <button className="export-btn" onClick={()=>{handleDownloadCSV()}}>Export</button>
                    </div>
                </div>:''}
                <div className="recipients-wrap">
                    <div className="recipients">
                        {/* <div className="top-links"> */}
                            {/* <div className="left-block">
                                <div className="link">
                                    <span>1</span>
                                    <span>Lists</span>
                                </div>
                                <div className="link">
                                    <span>8</span>
                                    <span>Bounced</span>
                                </div>
                                <div className="link">
                                    <span>1</span>
                                    <span>Open Leads</span>
                                </div>
                            </div> */}
                            {/* <div className="right-block">
                            <div className="link">
                                <a href={`/campaign/${campaignID}/add-recipients`} className="primary-btn">Add Recipients</a>
                            </div>
                            </div> */}
                        {/* </div> */}

                        <div className="recipient-data-block">
                            <div className="recipient-filter">
                                <div className="field-wrap">
                                    <input 
                                        type="text" 
                                        name="text"
                                        placeholder="Seach Recipients" 
                                        value={payload.text}
                                        onChange={handleFilter}
                                    />
                                </div>
                                <div className="field-wrap">
                                    <select className="selectbox" name="eventType" value={payload.eventType} onChange={handleFilter}>
                                        <option value="">Filter</option>
                                        <option value="lead">Lead</option>
                                        <option value="open">Open</option>
                                        <option value="click">Click</option>
                                        <option value="reply">Reply</option>
                                        <option value="bounce">Bounce</option>
                                    </select>
                                </div>
                                <div className="field-wrap">
                                    <select className="selectbox"  name="listID" value={payload.listID} onChange={handleFilter}>
                                        <option value="">All</option>
                                        {contactLists.map((row,i)=>(<option key={i} value={row.id}>{row.name}</option>))}
                                    </select>
                                </div>
                            </div>

                            {pageLoader ? <div className="page-laoder" style={{background:"white",minHeight:'500px'}}><BlockLoader/></div> : <table>
                                <thead>
                                    <tr>
                                        <th>
                                            <input type="checkbox" name="select-all" checked={isAllChecked} onChange={(e)=>{handleSelectAll(e)}} />
                                        </th>
                                        <th>&nbsp;</th>
                                        {Object.keys(recipients).length ? ((recipients.display_keys).map((th,i)=>(<th key={i}>{ typeof RECIPIENT_HEADER[th] !== 'undefined' ? RECIPIENT_HEADER[th] : th }</th>))):''}        
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.keys(recipients).length ? (recipients?.contacts.length ? (recipients?.contacts).map((row,i)=>(<Recipient key={i} row={row} dispalyKeys={recipients.display_keys} handleCheckboxChange = {handleCheckboxChange} campaignID={campaignID} />)):<tr><td colSpan={8}>Data not Found</td></tr>):<tr>
                                        <td colSpan={8}><BlockLoader/></td>
                                        </tr>}
                                    
                                </tbody>
                            </table>}
                            { totalCount > limit && <ReactPaginate 
                                breakLabel="..." 
                                nextLabel="next >" 
                                pageRangeDisplayed={5} 
                                previousLabel="< previous"
                                pageCount={Math.max(1, Math.ceil(totalCount/limit))}
                                onPageChange={onPageChange}
                                containerClassName="pagination"
                                activeClassName="active"
                                disabledClassName="disabled"
                                forcePage={forcePage}
                            />}
                            
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <Footer/>
    </>);
}

export default CampaignRecipients;